import React, { useEffect, useMemo, useRef } from "react";

function V3TaxBrandMark() {
  return (
    <div className="relative grid h-[52px] w-[52px] flex-shrink-0 place-items-center overflow-hidden rounded-[18px] border border-slate-200/85 bg-[linear-gradient(135deg,#2563eb_0%,#4f46e5_55%,#7c3aed_100%)] shadow-[0_14px_30px_rgba(79,70,229,0.20)]">
      <svg
        className="relative z-10 h-[22px] w-[22px] text-white"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
    </div>
  );
}

export function buildAnalysisErrorId(error = null) {
  const code = String(error?.code || "ANALYSIS_FAILED")
    .replace(/[^a-zA-Z0-9_-]/g, "")
    .slice(0, 20);
  const dataset = error?.datasetId ? String(error.datasetId) : "0";
  const stamp = error?.ts
    ? new Date(error.ts).getTime().toString(36)
    : Date.now().toString(36);
  return `V3T-${dataset}-${code}-${stamp.slice(-8)}`;
}

/** @deprecated Use buildAnalysisErrorId */
export const buildAnalysisReferenceId = buildAnalysisErrorId;

export function buildAnalysisSupportDescription(error = null, language = "de") {
  const isEnglish = String(language || "").toLowerCase().startsWith("en");
  const ref = buildAnalysisErrorId(error);
  const lines = [
    isEnglish ? `Error ID: ${ref}` : `Fehler-ID: ${ref}`,
    error?.datasetId ? `datasetId: ${error.datasetId}` : null,
    error?.taxYear ? `year: ${error.taxYear}` : null,
    error?.step ? `step: ${error.step}` : null,
    error?.code ? `code: ${error.code}` : null,
    error?.jobId ? `jobId: ${error.jobId}` : null,
    error?.source ? `source: ${error.source}` : null,
    error?.detail ? `detail: ${String(error.detail).slice(0, 1200)}` : null,
  ].filter(Boolean);

  const header = isEnglish
    ? "Calculation could not be completed."
    : "Berechnung konnte nicht abgeschlossen werden.";

  return `${header}\n\n${lines.join("\n")}`;
}

export default function AnalysisFailureModal({
  language = "de",
  error = null,
  onRetry,
  onBack,
  onSupport,
}) {
  const isEnglish = String(language || "").toLowerCase().startsWith("en");
  const retryRef = useRef(null);

  const labels = {
    eyebrow: isEnglish ? "System notice" : "Systemhinweis",
    title: isEnglish ? "Calculation failed" : "Berechnung fehlgeschlagen",
    statusBadge: isEnglish ? "Not completed" : "Nicht abgeschlossen",
    hintLines: isEnglish
      ? [
          "The calculation did not complete successfully.",
          "Restart the calculation; if the error occurs again: contact support.",
        ]
      : [
          "Die Berechnung wurde nicht erfolgreich abgeschlossen.",
          "Berechnung erneut starten, bei erneutem Fehler: Support kontaktieren.",
        ],
    errorId: isEnglish ? "Error ID" : "Fehler-ID",
    retry: isEnglish ? "Try again" : "Erneut starten",
    back: isEnglish ? "Back to start" : "Zurück zur Startseite",
    support: isEnglish ? "Contact support" : "Support kontaktieren",
    details: isEnglish ? "Technical details" : "Technische Details",
    dataset: "datasetId",
    year: "year",
    step: "step",
    code: "code",
    detail: "detail",
  };

  const errorId = useMemo(() => buildAnalysisErrorId(error), [error]);

  const detail = String(error?.detail || error?.message || "").trim();

  const technicalLines = useMemo(() => {
    return [
      `${labels.errorId}: ${errorId}`,
      error?.datasetId ? `${labels.dataset}: ${String(error.datasetId)}` : null,
      error?.taxYear ? `${labels.year}: ${String(error.taxYear)}` : null,
      error?.step ? `${labels.step}: ${String(error.step)}` : null,
      error?.code ? `${labels.code}: ${String(error.code)}` : null,
      error?.jobId ? `jobId: ${String(error.jobId)}` : null,
      detail ? `${labels.detail}: ${detail.slice(0, 800)}` : null,
    ].filter(Boolean);
  }, [error, detail, labels, errorId]);

  useEffect(() => {
    retryRef.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 z-[140] animate-overlay-fade-in print:hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="analysis-failure-title"
    >
      <div className="absolute inset-0 bg-slate-950/86 backdrop-blur-[6px]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.30),rgba(2,6,23,0.50))]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(15,23,42,0)_0%,rgba(15,23,42,0.14)_46%,rgba(2,6,23,0.56)_100%)]" />
      <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_220px_rgba(2,6,23,0.60)]" />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative z-10 w-full max-w-[580px] overflow-hidden rounded-[28px] border border-white/85 bg-white/[0.985] shadow-[0_36px_100px_rgba(2,6,23,0.40)] ring-1 ring-black/[0.03] animate-overlay-in will-change-[transform,opacity]">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.86),rgba(255,255,255,0.97))]" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
          <div className="pointer-events-none absolute -top-20 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full bg-slate-100/40 blur-3xl" />

          <div className="relative z-10 px-5 py-5 sm:px-6 sm:py-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3.5">
                <V3TaxBrandMark />

                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    {labels.eyebrow}
                  </div>
                  <h2
                    id="analysis-failure-title"
                    className="truncate text-[1.08rem] font-semibold tracking-tight text-slate-900 sm:text-[1.14rem]"
                  >
                    {labels.title}
                  </h2>
                </div>
              </div>

              <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-slate-200/80 bg-white/90 px-3 py-1.5 text-[11px] font-medium text-slate-600 shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                {labels.statusBadge}
              </span>
            </div>

            <div className="mt-5 rounded-[24px] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(248,250,252,0.92),rgba(255,255,255,0.96))] px-4 py-3.5 shadow-[0_8px_24px_rgba(15,23,42,0.05)] sm:px-5">
              <div className="space-y-1 text-sm leading-6 text-slate-600">
                {labels.hintLines.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </div>

            <div className="mt-5 space-y-2.5">
              <button
                ref={retryRef}
                type="button"
                onClick={onRetry}
                className="inline-flex w-full items-center justify-center gap-2 rounded-[18px] border border-transparent bg-[linear-gradient(135deg,#2563eb_0%,#4f46e5_55%,#7c3aed_100%)] px-4 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(79,70,229,0.24)] transition hover:translate-y-[-1px] hover:shadow-[0_16px_36px_rgba(79,70,229,0.28)] focus:outline-none focus:ring-4 focus:ring-indigo-100"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v6h6M20 20v-6h-6M20 8a8 8 0 0 0-14.9-4M4 16a8 8 0 0 0 14.9 4"
                  />
                </svg>
                {labels.retry}
              </button>

              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex items-center justify-center gap-2 rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-100"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                    />
                  </svg>
                  {labels.back}
                </button>

                {onSupport ? (
                  <button
                    type="button"
                    onClick={onSupport}
                    className="inline-flex items-center justify-center gap-2 rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-100"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.625 9.75a3.375 3.375 0 1 1 6.75 0c0 1.875-1.125 2.625-2.25 3.375-.75.5-1.125.875-1.125 1.875M12 18.75h.008M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                      />
                    </svg>
                    {labels.support}
                  </button>
                ) : null}
              </div>
            </div>

            {technicalLines.length > 0 ? (
              <details className="mt-4 rounded-[20px] border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">
                <summary className="cursor-pointer select-none font-semibold text-slate-700">
                  {labels.details}
                </summary>

                <div className="mt-3 space-y-1 font-mono text-[11px] leading-5">
                  {technicalLines.map((line) => (
                    <div key={line} className="break-words">
                      {line}
                    </div>
                  ))}
                </div>
              </details>
            ) : null}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes overlay-fade-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes overlay-in {
          0% {
            opacity: 0;
            transform: translateY(8px) scale(0.992);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-overlay-fade-in {
          animation: overlay-fade-in 0.30s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .animate-overlay-in {
          animation: overlay-in 0.44s cubic-bezier(0.22, 1, 0.36, 1);
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-overlay-fade-in,
          .animate-overlay-in {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
